import { SSO_URL, SSO_APP_NAME } from "../../utils/contextHelpers";
import { StorageKeys, session } from "../../utils/storage";
import { getCompany } from "../../config/companyConfig";
import { request } from "./core";
import { asociarCotizacion } from "./cotizacionService";

export const resetAffariUserContext = () => {
    session.remove(StorageKeys.CLIENTE_CODIGO);
    session.remove(StorageKeys.LISTA_PRECIO_BASE);
    session.remove(StorageKeys.ALMACEN);
    session.remove(StorageKeys.APP_CANAL);
    session.remove(StorageKeys.APP_ROL_CREDITO);
};

export const getAffariCanal = () => {
    const stored = String(session.get(StorageKeys.APP_CANAL) || "").trim().toUpperCase();
    return stored === "B2B" || stored === "B2C" ? stored : getCompany().defaultCanal;
};

export const getAffariClienteCodigo = () => {
    const company = getCompany();
    return String(session.get(StorageKeys.CLIENTE_CODIGO) || company.defaultClientCode).trim() || company.defaultClientCode;
};

export const getAffariListaPrecioBase = () => {
    const stored = String(session.get(StorageKeys.LISTA_PRECIO_BASE) || "").trim();
    return stored || getCompany().defaultPriceListBase;
};

export const getAffariListaPrecioBaseRaw = () =>
    String(session.get(StorageKeys.LISTA_PRECIO_BASE) || "").trim();

export const getAffariAlmacen = () => {
    const stored = String(session.get(StorageKeys.ALMACEN) || "").trim();
    return stored || getCompany().defaultWarehouse;
};

export const getAffariAlmacenRaw = () =>
    String(session.get(StorageKeys.ALMACEN) || "").trim();

export const setAffariUserContext = ({ clienteCodigo = "", listaPrecioBase = "", almacen = "" } = {}) => {
    const normalizedClienteCodigo = String(clienteCodigo || "").trim();
    const company = getCompany();
    const normalizedListaPrecioBase = String(listaPrecioBase || "").trim() || company.defaultPriceListBase;
    const normalizedAlmacen = String(almacen || "").trim() || company.defaultWarehouse;

    if (normalizedClienteCodigo) {
        session.set(StorageKeys.CLIENTE_CODIGO, normalizedClienteCodigo);
    }
    if (normalizedListaPrecioBase) {
        session.set(StorageKeys.LISTA_PRECIO_BASE, normalizedListaPrecioBase);
    }
    if (normalizedAlmacen) {
        session.set(StorageKeys.ALMACEN, normalizedAlmacen);
    }
};



export const buildAffariListaPrecioCodigo = (almacen = "") => {
    const resolvedAlmacen = String(almacen || getAffariAlmacen() || "").trim() || getCompany().defaultWarehouse;
    return `${getAffariListaPrecioBase()}-${resolvedAlmacen}`;
};

export const ssoUser = () => request(`${SSO_URL}user`);

// atributos del SSO: string JSON u objeto — { "<empresaId>": { "ECOMMERCE": { perfil, canal, clienteCodigo, listaPrecioBase, almacen, rolCredito } } }
const getSsoAppData = (ssoResponse) => {
    let attrs = {};
    try {
        attrs = typeof ssoResponse?.atributos === "string" ? JSON.parse(ssoResponse.atributos) : (ssoResponse?.atributos || {});
    } catch {
        attrs = {};
    }
    if (!attrs || typeof attrs !== "object") return null;
    const empresaKey = String(ssoResponse?.empresa ?? "").trim();
    if (empresaKey && attrs[empresaKey]?.[SSO_APP_NAME]) {
        return attrs[empresaKey][SSO_APP_NAME];
    }
    for (const key of Object.keys(attrs)) {
        if (attrs[key]?.[SSO_APP_NAME]) {
            return attrs[key][SSO_APP_NAME];
        }
    }
    return null;
};

export const establishSSOSession = async () => {
    const ssoResponse = await ssoUser();
    const ssoUsername = String(ssoResponse?.usuario || "").trim();
    if (!ssoUsername) {
        throw new Error("Respuesta inválida del SSO.");
    }
    if (SSO_APP_NAME && Array.isArray(ssoResponse?.apps) && ssoResponse.apps.length > 0 && !ssoResponse.apps.includes(SSO_APP_NAME)) {
        const err = new Error("Su cuenta no tiene acceso a esta aplicación.");
        err.appBlocked = true;
        throw err;
    }

    const appData = getSsoAppData(ssoResponse);
    const roles = Array.isArray(ssoResponse?.roles) ? ssoResponse.roles : [];
    const appPerfil
        = String(appData?.perfil || "").trim()
        || (roles.some(r => r === "ROLE_ADMINISTRADOR" || r?.authority === "ROLE_ADMINISTRADOR") ? "Administrador"
        : roles.some(r => r === "ROLE_CLIENTE" || r?.authority === "ROLE_CLIENTE") ? "Cliente"
        : "");
    if (!appPerfil) {
        const err = new Error("Su cuenta no tiene acceso a esta plataforma.");
        err.roleBlocked = true;
        throw err;
    }

    session.set(StorageKeys.AUTH_ID, String(ssoResponse?.id ?? ""));
    session.set(StorageKeys.AUTH_USERNAME, ssoUsername);
    if (String(appData?.clienteCodigo || "").trim()) session.set(StorageKeys.CLIENTE_CODIGO, String(appData.clienteCodigo).trim());
    if (String(appData?.listaPrecioBase || "").trim()) session.set(StorageKeys.LISTA_PRECIO_BASE, String(appData.listaPrecioBase).trim());
    if (String(appData?.almacen || "").trim()) session.set(StorageKeys.ALMACEN, String(appData.almacen).trim());
    if (String(appData?.canal || "").trim()) session.set(StorageKeys.APP_CANAL, String(appData.canal).trim().toUpperCase());
    if (String(appData?.rolCredito || "").trim()) session.set(StorageKeys.APP_ROL_CREDITO, String(appData.rolCredito).trim().toUpperCase());

    return {
        username: ssoUsername,
        appPerfil,
        appCanal: getAffariCanal(),
        rolCredito: String(appData?.rolCredito || "").trim().toUpperCase(),
        asociarCotizacion: await asociarStoredCotizacion(getAffariClienteCodigo()),
    };
};

const asociarStoredCotizacion = async (clienteCodigo) => {
    const resolvedClienteCodigo = String(clienteCodigo || getAffariClienteCodigo()).trim();
    const id = String(session.get(StorageKeys.COTIZACION_ID) || "").trim();
    if (!resolvedClienteCodigo || !id) return false;
    await asociarCotizacion(resolvedClienteCodigo, id);
    session.set(StorageKeys.COTIZACION_ID, id);
    return id;
};

export const ensureCatalogAuth = async () => {
    const buildDefaultGuestContext = (id = null) => {
        const company = getCompany();
        const defaultCanal = company.defaultCanal || "";
        session.set(StorageKeys.APP_CANAL, defaultCanal);
        session.remove(StorageKeys.APP_ROL_CREDITO);
        return {
            id,
            clienteCodigo: company.defaultClientCode,
            listaPrecioBase: company.defaultPriceListBase,
            almacen: company.defaultWarehouse,
            catalogoTipo: defaultCanal,
            appCanal: defaultCanal,
            discardedCotizacion: false,
        };
    };

    const currentUsername = session.get(StorageKeys.AUTH_USERNAME);
    const currentId = session.get(StorageKeys.AUTH_ID);

    // sesión establecida por el SSO web (sin contraseña local): contexto desde valores almacenados
    if (currentUsername && currentId) {
        const appCanal = getAffariCanal();
        return {
            id: currentId,
            clienteCodigo: getAffariClienteCodigo(),
            listaPrecioBase: getAffariListaPrecioBase(),
            almacen: getAffariAlmacen(),
            catalogoTipo: appCanal,
            appCanal,
            discardedCotizacion: false,
        };
    }

    return buildDefaultGuestContext(null);
};

/*
// ── Legacy: login REST con Basic Auth (reemplazado por el SSO web) ──
// Para reactivar: re-importar API_URL desde contextHelpers y { api, deduplicate, buildQuery } desde ./core
export const loginService = (username, password) => {
    const key = `login-${username}-${password}`;
    return deduplicate(key, () => {
        const options = { method: "POST" };
        return request(`${SSO_URL}login?${buildQuery({
            password: encodeURIComponent(password),
            username: encodeURIComponent(username),
        })}`, options);
    });
};

export const user = api("user", () => `${API_URL}user`);

const loadAffariUserContext = async () => {
    const response = await user();
    const clienteCodigo = String(response?.cliente?.codigo || "").trim();
    const listaPrecioBase = String(response?.listaPrecio || "").trim();
    const almacenDefault = String(response?.almacen || "").trim();
    const appCanal = String(response?.canal || "").trim().toUpperCase();
    const rolCredito = String(response?.rolCredito || "").trim().toUpperCase();
    const appPerfil
        = response?.roles?.some(r => r.authority === "ROLE_ADMINISTRADOR") ? "Administrador"
        : response?.roles?.some(r => r.authority === "ROLE_CLIENTE") ? "Cliente"
        : null;
    if (!appPerfil) {
        const err = new Error("Su cuenta no tiene acceso a esta plataforma.");
        err.roleBlocked = true;
        throw err;
    }

    session.set(StorageKeys.CLIENTE_CODIGO, clienteCodigo);
    session.set(StorageKeys.LISTA_PRECIO_BASE, listaPrecioBase);
    session.set(StorageKeys.ALMACEN, almacenDefault);
    session.set(StorageKeys.APP_CANAL, appCanal);
    session.set(StorageKeys.APP_ROL_CREDITO, rolCredito);

    return {
        clienteCodigo,
        listaPrecioBase,
        almacen: almacenDefault,
        catalogoTipo: appCanal,
        appCanal,
        appPerfil,
        rolCredito,
        profile: response,
    };
};

// Rama legacy de ensureCatalogAuth con credenciales (username/password almacenados):
// if (currentUsername && currentPassword && currentId) {
//     const isGuestSession = currentUsername === "guest" && currentPassword === "guest";
//     if (isGuestSession) {
//         resetAffariUserContext();
//         return buildDefaultGuestContext(currentId);
//     }
//     const affariContext = await loadAffariUserContext();
//     return { id: currentId, ...affariContext, discardedCotizacion: false };
// }
*/